/**
 * The marketplace landing: a hero, one panel per partner retailer, and the
 * strip of reasons along the bottom. The supplied Massmart design.
 *
 * **The retailers are data; their livery is not.** Which wholesalers appear,
 * and which three products sit in each "Popular at …" row, come from the server
 * (`featured_rank`). What a Makro panel looks like — black, the stripes, "Big
 * brands. Bigger business." — is presentation, and lives in `RETAILERS` below.
 * A wholesaler with no entry there is still on the marketplace, in the grid
 * under this; it simply has no branded panel.
 *
 * Matched on the slug's first word, because the slug is counted to make it
 * unique ("makro", "makro-2") and a trading name can grow ("Jumbo Cash and
 * Carry"), while the first word is the brand a merchant recognises.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BadgePercent,
  BarChart3,
  Boxes,
  Building2,
  CheckCircle2,
  LayoutGrid,
  Package,
  PackageOpen,
  ShieldCheck,
  ShoppingCart,
  Store,
  Tags,
  Truck,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import hero from "@/assets/marketplace-hero.webp";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import {
  marketplaceRepository,
  type Listing,
  type Supplier,
} from "@/repositories/marketplaceRepository";

import { useAddToBasket } from "./useAddToBasket";

interface Retailer {
  key: string;
  name: string;
  wordmark: ReactNode;
  /** The compact logo for the supplier chips and product cards. */
  mark: ReactNode;
  strapline: string;
  pitch: ReactNode;
  benefits: [LucideIcon, string][];
  /** Header band and the Shop button. */
  band: string;
  button: string;
  /** Icon colour in the benefit row. */
  accent: string;
}

const RETAILERS: Retailer[] = [
  {
    key: "jumbo",
    name: "Jumbo",
    wordmark: (
      <span className="text-4xl font-black uppercase tracking-tight text-black sm:text-5xl">
        Jumbo
      </span>
    ),
    mark: (
      <span className="rounded bg-[#FFC20E] px-2 py-0.5 text-sm font-black uppercase tracking-tight text-black">
        Jumbo
      </span>
    ),
    strapline: "Everyday low prices for your business",
    pitch: "Trusted Brands. Wholesale Value.",
    benefits: [
      [BadgePercent, "Exclusive Wholesale Prices"],
      [Boxes, "Wide Product Range"],
      [Truck, "Bulk Stock Always Available"],
    ],
    band: "bg-[#FFC20E] text-black",
    button: "bg-[#FFC20E] text-black hover:bg-[#F2B600]",
    accent: "text-danger-500",
  },
  {
    key: "makro",
    name: "Makro",
    wordmark: (
      <span className="flex items-center gap-2">
        <span className="text-4xl font-extrabold lowercase tracking-tight text-white sm:text-5xl">
          makro
        </span>
        {/* The three stripes. Skewed bars rather than a logo file, so the
            panel stays sharp at any size. */}
        <span className="flex -skew-x-12 gap-1" aria-hidden>
          <span className="h-8 w-1.5 bg-[#FFC20E]" />
          <span className="h-8 w-1.5 bg-[#E4032E]" />
          <span className="h-8 w-1.5 bg-[#0071CE]" />
        </span>
      </span>
    ),
    mark: (
      <span className="flex items-center gap-1 rounded bg-black px-2 py-0.5">
        <span className="text-sm font-extrabold lowercase tracking-tight text-white">
          makro
        </span>
        <span className="flex -skew-x-12 gap-px" aria-hidden>
          <span className="h-3 w-0.5 bg-[#FFC20E]" />
          <span className="h-3 w-0.5 bg-[#E4032E]" />
          <span className="h-3 w-0.5 bg-[#0071CE]" />
        </span>
      </span>
    ),
    strapline: "Big brands. Bigger business.",
    pitch: (
      <>
        More Choice. More Value{" "}
        <span className="text-[#FFC20E]">for Your Spaza.</span>
      </>
    ),
    benefits: [
      [Tags, "Competitive Wholesale Deals"],
      [PackageOpen, "Bulk Buying Made Easy"],
      [Store, "Exclusive Business Range"],
    ],
    band: "bg-black text-white",
    button: "bg-black text-white hover:bg-navy-900",
    accent: "text-danger-500",
  },
  {
    key: "shield",
    name: "Shield",
    wordmark: (
      <span className="flex items-center gap-2 text-white">
        <ShieldCheck className="size-9 sm:size-10" aria-hidden />
        <span className="text-4xl font-black italic tracking-tight sm:text-5xl">
          Shield
        </span>
      </span>
    ),
    mark: (
      <span className="flex items-center gap-1 rounded bg-[#0B4EA2] px-2 py-0.5 text-white">
        <ShieldCheck className="size-3.5" aria-hidden />
        <span className="text-sm font-black italic tracking-tight">Shield</span>
      </span>
    ),
    strapline: "Quality brands. Stronger communities.",
    pitch: (
      <>
        Everyday Essentials{" "}
        <span className="text-[#FFC20E]">
          for a Thriving Township Economy.
        </span>
      </>
    ),
    benefits: [
      [BadgePercent, "Great Prices on Essentials"],
      [Package, "Consistent Availability"],
      [Users, "Trusted Retail Partner"],
    ],
    band: "bg-[#0B4EA2] text-white",
    button: "bg-[#0B4EA2] text-white hover:bg-[#093F85]",
    accent: "text-[#0B4EA2]",
  },
];

function retailerFor(supplier: Supplier): Retailer | undefined {
  const brand = supplier.slug.split("-")[0];
  return RETAILERS.find((retailer) => retailer.key === brand);
}

/**
 * The retailer's compact logo, or its name for a wholesaler with no livery.
 * Labelled with the name, so a screen reader hears "Makro" and not nothing.
 */
export function RetailerMark({ supplier }: { supplier: Supplier }) {
  const retailer = retailerFor(supplier);
  if (!retailer) return <>{supplier.display_name}</>;
  return (
    <span role="img" aria-label={supplier.display_name} className="inline-flex">
      {retailer.mark}
    </span>
  );
}

export function MarketplaceShowcase({
  suppliers,
  onShop,
}: {
  suppliers: Supplier[];
  onShop: (supplierId: string) => void;
}) {
  const featured = useQuery({
    queryKey: ["marketplace", "products", "featured"],
    queryFn: () => marketplaceRepository.products({ featured: true }),
  });

  // In the design's order, not the API's alphabetical one.
  const panels = RETAILERS.flatMap((retailer) => {
    const supplier = suppliers.find((entry) => retailerFor(entry) === retailer);
    return supplier ? [{ retailer, supplier }] : [];
  });

  return (
    <div className="space-y-5">
      <Hero />

      {panels.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {panels.map(({ retailer, supplier }) => (
            <RetailerPanel
              key={supplier.id}
              retailer={retailer}
              listings={
                featured.data?.results
                  .filter((listing) => listing.wholesaler.id === supplier.id)
                  .slice(0, 3) ?? []
              }
              loading={featured.isLoading}
              onShop={() => onShop(supplier.id)}
            />
          ))}
        </div>
      )}

      <ValueStrip retailers={panels.map(({ retailer }) => retailer.name)} />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden rounded-card bg-surface shadow-card">
      <img
        src={hero}
        alt=""
        className="absolute inset-y-0 right-0 hidden h-full w-3/5 object-cover object-left md:block"
      />
      {/* Fades the photograph into the headline so the text never sits on it. */}
      <div
        className="absolute inset-y-0 right-0 hidden w-3/5 bg-gradient-to-r from-surface via-surface/40 to-transparent md:block"
        aria-hidden
      />
      <div className="relative px-5 py-6 md:w-3/5 md:py-8 lg:px-8">
        <h1 className="text-3xl font-black leading-tight tracking-tight text-navy-950 sm:text-4xl xl:text-5xl">
          Wholesale Access for a{" "}
          <span className="text-accent-orange">Stronger Tomorrow</span>
        </h1>
        <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm font-semibold text-ink-900">
          {[
            "Top Retailer Partners",
            "Bulk Prices",
            "Reliable Supply",
            "Built for Township Merchants",
          ].map((point, index) => (
            <li key={point} className="flex items-center gap-3">
              {index > 0 && <span aria-hidden>•</span>}
              {point}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function RetailerPanel({
  retailer,
  listings,
  loading,
  onShop,
}: {
  retailer: Retailer;
  listings: Listing[];
  loading: boolean;
  onShop: () => void;
}) {
  return (
    <section
      aria-label={retailer.name}
      className="flex flex-col overflow-hidden rounded-card border border-line bg-surface shadow-card"
    >
      <header
        className={cn(
          "flex items-center gap-4 px-5 py-4",
          retailer.band,
        )}
      >
        <div className="min-w-0">
          {retailer.wordmark}
          <p className="mt-1 text-[11px] font-bold uppercase tracking-wide">
            {retailer.strapline}
          </p>
        </div>
        <p className="ml-auto max-w-[9rem] border-l border-current/30 pl-4 text-sm font-bold leading-snug">
          {retailer.pitch}
        </p>
      </header>

      <ul className="grid grid-cols-1 gap-2 border-b border-line px-4 py-3 sm:grid-cols-3">
        {retailer.benefits.map(([Icon, label]) => (
          <li key={label} className="flex items-center gap-2 text-xs text-ink-600">
            <Icon className={cn("size-6 shrink-0", retailer.accent)} aria-hidden />
            <span className="leading-tight">{label}</span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between px-4 pt-3">
        <h2 className="text-base font-semibold text-ink-900">
          Popular at {retailer.name}
        </h2>
        <button
          type="button"
          onClick={onShop}
          className="flex items-center gap-1 text-xs font-medium text-info-500 hover:underline"
        >
          View All Products <ArrowRight className="size-3.5" aria-hidden />
        </button>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-2 p-3 sm:grid-cols-3">
        {loading
          ? [0, 1, 2].map((slot) => (
              <div key={slot} className="h-60 animate-pulse rounded-control bg-canvas" />
            ))
          : listings.map((listing) => (
              <MiniCard key={listing.id} listing={listing} />
            ))}
      </div>

      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={onShop}
          className={cn(
            "flex h-touch w-full items-center justify-center gap-2 rounded-control text-base font-semibold transition-colors",
            retailer.button,
          )}
        >
          <ShoppingCart className="size-5" aria-hidden />
          Shop {retailer.name}
          <ArrowRight className="size-5" aria-hidden />
        </button>
      </div>
    </section>
  );
}

function MiniCard({ listing }: { listing: Listing }) {
  const add = useAddToBasket(listing);

  return (
    <div className="flex flex-col rounded-control border border-line p-2">
      <div className="relative flex h-24 items-center justify-center sm:h-28">
        {listing.image ? (
          <img
            src={listing.image}
            alt=""
            className="max-h-full max-w-full object-contain"
            loading="lazy"
          />
        ) : (
          <Package className="size-8 text-ink-400" aria-hidden />
        )}
        {listing.is_special && (
          <span className="absolute left-0 top-0 rounded bg-danger-500 px-1 text-[10px] font-bold text-white">
            SAVE {formatMoney(listing.saving)}
          </span>
        )}
      </div>
      <p className="mt-2 line-clamp-3 flex-1 text-xs text-ink-900">
        {listing.name}
      </p>
      <p className="mt-1 text-base font-bold tabular-nums text-danger-500">
        {formatMoney(listing.price)}
      </p>
      <button
        type="button"
        onClick={() => add.mutate()}
        disabled={!listing.in_stock || add.isPending}
        className="mt-2 flex h-8 items-center justify-center gap-1.5 rounded-control border border-line text-sm font-medium text-ink-900 hover:bg-canvas disabled:opacity-50"
      >
        {add.isSuccess ? (
          <CheckCircle2 className="size-4 text-money-600" aria-hidden />
        ) : (
          <ShoppingCart className="size-4" aria-hidden />
        )}
        {listing.in_stock ? (add.isSuccess ? "Added" : "Add") : "Out"}
      </button>
    </div>
  );
}

function ValueStrip({ retailers }: { retailers: string[] }) {
  const items: [LucideIcon, string, string][] = [
    [
      LayoutGrid,
      "Access Top Retailers",
      retailers.length > 0 ? retailers.join(" | ") : "Wholesalers near you",
    ],
    [BadgePercent, "Exclusive Wholesale Prices", "Increase your margins"],
    [Truck, "Bulk Ordering Made Easy", "Get more stock, sell more"],
    [BarChart3, "Real-Time Inventory", "See stock availability"],
    [Building2, "Built for Township Merchants", "Grow your business with SmartSpaza"],
  ];

  return (
    <ul className="grid gap-4 rounded-card border border-line bg-surface px-5 py-4 shadow-card sm:grid-cols-2 xl:grid-cols-5">
      {items.map(([Icon, title, detail]) => (
        <li key={title} className="flex items-center gap-3">
          <Icon className="size-8 shrink-0 text-navy-800" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-ink-900">{title}</p>
            <p className="text-xs text-ink-500">{detail}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
